/**
 * ExportDocumentsModal Component
 *
 * Modal for exporting documents from a CaseFile.
 * Allows users to download individual documents or select multiple for batch download.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  X,
  Download,
  FileText,
  FileImage,
  File,
  Loader2,
  CheckSquare,
  Square,
  AlertCircle,
} from 'lucide-react';

interface DocumentSummary {
  documentId: string;
  caseFileId: string;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  category?: string;
  uploadedAt: { _seconds: number };
}

interface ListDocumentsResponse {
  success: boolean;
  documents: DocumentSummary[];
  totalCount: number;
}

interface GetDocumentResponse {
  success: boolean;
  downloadUrl: string;
}

interface ExportDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseFileId: string;
  caseFileName: string;
}

function getFileIcon(fileType: string): React.ReactNode {
  const type = fileType.toLowerCase();
  if (type.includes('pdf')) {
    return <FileText size={20} className="text-red-500" />;
  }
  if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg')) {
    return <FileImage size={20} className="text-blue-500" />;
  }
  if (type.includes('doc') || type.includes('word')) {
    return <FileText size={20} className="text-blue-600" />;
  }
  return <File size={20} className="text-muted" />;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function ExportDocumentsModal({
  isOpen,
  onClose,
  caseFileId,
  caseFileName,
}: ExportDocumentsModalProps): JSX.Element | null {
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [downloadAllInProgress, setDownloadAllInProgress] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!caseFileId) return;

    setLoading(true);
    setError(null);

    try {
      const listDocuments = httpsCallable<
        { caseFileId: string },
        ListDocumentsResponse
      >(functions, 'listDocuments');

      const result = await listDocuments({ caseFileId });

      if (result.data.success) {
        setDocuments(result.data.documents);
      } else {
        setError('Failed to load documents');
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [caseFileId]);

  useEffect(() => {
    if (isOpen) {
      fetchDocuments();
      setSelectedDocIds(new Set());
    }
  }, [isOpen, fetchDocuments]);

  const handleDownloadSingle = async (documentId: string, fileName: string) => {
    setDownloadingIds((prev) => new Set(prev).add(documentId));

    try {
      const getDocument = httpsCallable<
        { caseFileId: string; documentId: string },
        GetDocumentResponse
      >(functions, 'getDocument');

      const result = await getDocument({ caseFileId, documentId });

      if (result.data.success && result.data.downloadUrl) {
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = result.data.downloadUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setError('Failed to generate download link');
      }
    } catch (err) {
      console.error('Error downloading document:', err);
      setError(err instanceof Error ? err.message : 'Failed to download document');
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedDocIds.size === 0) return;

    setDownloadAllInProgress(true);

    const selectedDocs = documents.filter((doc) => selectedDocIds.has(doc.documentId));

    // Download each document sequentially with a small delay to avoid browser blocking
    for (const doc of selectedDocs) {
      await handleDownloadSingle(doc.documentId, doc.fileName);
      // Small delay between downloads to prevent browser from blocking
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setDownloadAllInProgress(false);
  };

  const handleDownloadAll = async () => {
    if (documents.length === 0) return;

    setDownloadAllInProgress(true);

    // Download each document sequentially with a small delay
    for (const doc of documents) {
      await handleDownloadSingle(doc.documentId, doc.fileName);
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setDownloadAllInProgress(false);
  };

  const toggleSelectAll = () => {
    if (selectedDocIds.size === documents.length) {
      setSelectedDocIds(new Set());
    } else {
      setSelectedDocIds(new Set(documents.map((d) => d.documentId)));
    }
  };

  const toggleDocumentSelection = (documentId: string) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  };

  if (!isOpen) return null;

  const allSelected = documents.length > 0 && selectedDocIds.size === documents.length;
  const someSelected = selectedDocIds.size > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-soft-neutral">
          <div>
            <h2 className="text-xl font-semibold text-ink">Export Documents</h2>
            <p className="text-sm text-muted mt-1">
              Download documents from {caseFileName}'s CaseFile
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost p-2 -mr-2"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-evergreen" />
              <span className="ml-3 text-muted">Loading documents...</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 p-4 bg-red-50 text-red-700 rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <div>
                <p className="font-medium">Error loading documents</p>
                <p className="text-sm">{error}</p>
              </div>
              <button onClick={fetchDocuments} className="btn-ghost btn-sm ml-auto">
                Retry
              </button>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted mb-4" />
              <h3 className="text-lg font-medium text-ink mb-2">No documents to export</h3>
              <p className="text-muted">
                Upload documents to your CaseFile first, then you can export them here.
              </p>
            </div>
          ) : (
            <>
              {/* Selection header */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-soft-neutral">
                <button
                  onClick={toggleSelectAll}
                  className="flex items-center gap-2 text-sm text-muted hover:text-ink transition-colors"
                >
                  {allSelected ? (
                    <CheckSquare className="w-5 h-5 text-evergreen" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
                <span className="text-sm text-muted">
                  {documents.length} document{documents.length !== 1 ? 's' : ''}
                  {someSelected && ` (${selectedDocIds.size} selected)`}
                </span>
              </div>

              {/* Documents list */}
              <div className="space-y-2">
                {documents.map((doc) => {
                  const isSelected = selectedDocIds.has(doc.documentId);
                  const isDownloading = downloadingIds.has(doc.documentId);

                  return (
                    <div
                      key={doc.documentId}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        isSelected
                          ? 'border-evergreen bg-evergreen/5'
                          : 'border-soft-neutral hover:border-stone-300'
                      }`}
                    >
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleDocumentSelection(doc.documentId)}
                        className="flex-shrink-0"
                        disabled={downloadAllInProgress}
                      >
                        {isSelected ? (
                          <CheckSquare className="w-5 h-5 text-evergreen" />
                        ) : (
                          <Square className="w-5 h-5 text-muted" />
                        )}
                      </button>

                      {/* File icon */}
                      <div className="flex-shrink-0">
                        {getFileIcon(doc.fileType)}
                      </div>

                      {/* File info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-ink truncate" title={doc.fileName}>
                          {doc.fileName}
                        </p>
                        <p className="text-xs text-muted">
                          {formatFileSize(doc.fileSizeBytes)}
                          {doc.category && ` • ${doc.category}`}
                        </p>
                      </div>

                      {/* Download button */}
                      <button
                        onClick={() => handleDownloadSingle(doc.documentId, doc.fileName)}
                        disabled={isDownloading || downloadAllInProgress}
                        className="btn-ghost btn-sm flex items-center gap-1"
                        title="Download this document"
                      >
                        {isDownloading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {documents.length > 0 && (
          <div className="px-6 py-4 border-t border-soft-neutral flex items-center justify-between gap-3">
            <p className="text-xs text-muted">
              Documents will download individually to your browser.
            </p>
            <div className="flex gap-2">
              {someSelected ? (
                <button
                  onClick={handleDownloadSelected}
                  disabled={downloadAllInProgress}
                  className="btn-primary flex items-center gap-2"
                >
                  {downloadAllInProgress ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download Selected ({selectedDocIds.size})
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleDownloadAll}
                  disabled={downloadAllInProgress}
                  className="btn-primary flex items-center gap-2"
                >
                  {downloadAllInProgress ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download All ({documents.length})
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExportDocumentsModal;
